import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroChassis from '../components/HeroChassis';
import SceneFrame from '../components/three/SceneFrame';
import HeroFilm, { heroFilmEnabled } from '../components/HeroFilm';
import FilmLightbox, { FILM_LENGTH, filmEnabled, numbersChapter } from '../components/FilmLightbox';
import { PILOT_MAILTO, SALES_MAILTO, appKit, dollars, platformFree, platformStart } from '../data/plans';
import { askNova } from '../components/ConsoleHeader';
import PitchButton from '../components/PitchButton';
import MEASURED from '../data/measurements.gen.json';
import SpineRun from '../components/landing/SpineRun';
import MeasureGlyph from '../components/landing/MeasureGlyph';
import { track } from '../utils/track';
import PageWorld from '../components/site/PageWorld';
import { LANDING } from '../data/worlds';
import { INVOICE, PRODUCTS } from '../data/spine';

export { PILOT_MAILTO };
export const DEMO_MAILTO = SALES_MAILTO;

// Public homepage, laid out to convert ([[homepage-conversion-first]]):
// one primary CTA (Start free) above the fold, measured trust under it,
// the buyer's jobs, the product running, three reasons, the two products
// (each a door into its page), the offer, a final CTA. Eight sections;
// prose stays short. The frozen rules hold
// ([[website-product-first-messaging]]): nQube + agentOS above the fold,
// two products at the offer, measured numbers only, no invented proof.
// The four moves (Bridge, Govern, Build, One brain) are the section
// kickers; the invoice replay is Govern; the products are Build. Edge OS
// on /partners, hardware on /store, the fleet strip on /app-kit, what's
// new on /platform. The invoice steps and product summaries are shared
// with /platform and /app-kit through data/spine.js.

const NVENTR_AI = 'https://nventr.ai';
const SALES_TO = '/contact?subject=Sales';

// Written as relief for the person who owns the risk, not as a feature
// list ([[website-product-first-messaging]] 2026-10-14, "the buyer's voice").
const JOBS = [
  {
    team: 'Finance',
    can: 'post approved invoices to the ERP',
    flow: 'The agent reads vendor, amount and terms. Anything over policy waits for a person. Only the runtime posts — logged, attributable.',
  },
  {
    team: 'Ops & support',
    can: 'triage tickets into the CRM',
    flow: 'Merge tickets, update the CRM, draft the credit memo. The agent drafts; a person or a rule says yes before anything changes.',
  },
  {
    team: 'Shared services',
    can: 'take claims from email and PDFs',
    flow: 'Claims, enrollment, document intake. Every commit carries the audit trail that survives “who approved this?”',
  },
];

// Three things a buyer can check in the console, not adjectives
// ([[website-product-first-messaging]] 2026-10-08, cut to one sentence
// each 2026-10-14).
const PROOFS = [
  {
    k: 'The model holds no pen',
    v: 'A write is a capability you declared; the model proposes, a person or a rule says yes, the runtime posts with its own credentials.',
    to: '/gate',
    cta: 'What a gate actually is',
  },
  {
    k: 'Every commit leaves a receipt',
    v: 'Who approved it, when, the policy version, a trace id, a hash of what was written, and an idempotency key — a retry cannot post twice.',
    to: '/platform/agentos',
    cta: 'agentOS',
  },
  {
    k: 'Small models do the steps',
    v: 'Four tiers, escalation only on a verified failure, caps on frontier share and spend, your own keys.',
    to: '/gate#routing',
    cta: 'How routing works',
  },
];

// Measured on nventr's own fleet — every value read from the same evidence
// file the film renders from (media/results-film/measurements.json, copied
// to data/measurements.gen.json by scripts/deploy-frontend.sh). A number
// appears only if its source is in that file. Three lead the trust band.
const fmtSec = (s) => `${Number(s).toFixed(1)} s`;
const MEASURES = [
  { g: 'cost', v: `${Math.round(MEASURED.compare.ratio)}×`, k: 'cheaper per run', d: 'Same workflow, same gates, same result — small models by tier vs one large model on every step.', src: MEASURED.compare.source },
  { g: 'claim', v: fmtSec(MEASURED.kit.claim.seconds), k: 'to claim an App Kit instance', d: 'URL, database, layer repo, seeded — then verified live.', src: MEASURED.kit.claim.source },
  { g: 'restart', v: fmtSec(MEASURED.fleet.restartMs / 1000), k: 'to restart a down service', d: `Reported down ${MEASURED.fleet.consecutiveReports}×, restarted by the fleet, on the record.`, src: MEASURED.fleet.source },
];

// Systems you already use — names, not adjectives, one row each. Every
// entry is a shipped seam ([[website-product-first-messaging]] 2026-10-13).
const WORKS_WITH = [
  { k: 'Systems', items: ['A declared REST or JSON API', 'Outgoing webhooks', 'Email (SMTP)', 'Slack & Teams webhooks', 'GraphQL on your data'] },
  { k: 'Knowledge', items: ['SharePoint', 'Confluence', 'Google Drive', 'Notion', 'S3', 'Website crawl', 'PDF, Word, text, CSV'] },
  { k: 'Models', items: ['OpenAI', 'Anthropic', 'xAI', 'Google', 'Groq', 'Together', 'OpenRouter', 'DeepSeek', 'Moonshot', 'fal.ai (Flux, Kling, Runway, Luma)', 'Image & video generation', 'vLLM / Ollama'] },
  { k: 'Runs on', items: ['Hosted on nventr', 'Your AWS / Supabase / MinIO', 'One-VM self-host', 'GitHub or Gitea'] },
];

// The NoVA prompt's placeholder cycles through four workflows people
// actually bring; stops while the field has focus or text.
const NEEDS = [
  'e.g. post approved invoices into NetSuite, anything over $10k waits for a person',
  'e.g. triage support tickets into our CRM, refunds over $500 wait for a lead',
  'e.g. take claims from email and PDFs, flag anything missing, a person approves payout',
  'e.g. publish the updated policy to our knowledge base after legal signs off',
];

// One tracked link per plan rung; the funnel splits on `plan`. The Kit
// button names the product and price here (its tier cta is the Pricing wording).
const KIT_LABEL = `Launch App Kit · ${dollars(appKit)}`;
function PlanLink({ tier, where, className = 'cs-btn cs-btn-primary', children }) {
  const label = children || (tier.id === 'kit' ? KIT_LABEL : tier.cta);
  return (
    <Link
      to={tier.href}
      className={className}
      onClick={() => track('cta_click', { where, plan: tier.id, label: typeof label === 'string' ? label : tier.cta })}
    >
      {label}
    </Link>
  );
}

// The quiet door from a product's card into its page (hero and #products).
// Tracked like a plan click so the funnel can see who reads before buying.
function HowLink({ product, where, className = 'cs-land-proof-link' }) {
  return (
    <Link
      to={product.to}
      className={className}
      onClick={() => track('cta_click', { where, plan: product.id === 'kit' ? 'kit' : 'start', label: product.how })}
    >
      {product.how} →
    </Link>
  );
}
const PRODUCT_BY_ID = Object.fromEntries(PRODUCTS.map((p) => [p.id, p]));
const MEASURE_BY_G = Object.fromEntries(MEASURES.map((m) => [m.g, m]));

function PlayGlyph() {
  return (
    <svg className="cs-land-play" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 8.2v7.6l6-3.8z" fill="currentColor" />
    </svg>
  );
}

export default function Landing() {
  const [need, setNeed] = useState('');
  const [needIdx, setNeedIdx] = useState(0);
  const [needFocus, setNeedFocus] = useState(false);
  useEffect(() => { track('home_view'); }, []);
  useEffect(() => {
    if (needFocus || need || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = window.setInterval(() => setNeedIdx((i) => (i + 1) % NEEDS.length), 4200);
    return () => window.clearInterval(id);
  }, [needFocus, need]);
  const askNeed = (e) => {
    e.preventDefault();
    const text = need.trim();
    if (!text) return;
    track('nova_ask');
    askNova(text);
    setNeed('');
  };
  // Scroll-reveal: sections fade/rise in once as they enter the viewport.
  // Opacity/transform only (layout is untouched, so anchor scrolling still
  // lands correctly); no-ops under prefers-reduced-motion or without IO.
  useEffect(() => {
    if (
      typeof IntersectionObserver === 'undefined'
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return undefined;
    const els = document.querySelectorAll('.cs-land .cs-store-section, .cs-land .cs-store-hero, .cs-land .cs-land-band');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('rv-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => {
      // Above-the-fold content paints immediately — no entry fade.
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.classList.add('rv');
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const filmOn = heroFilmEnabled();
  // The hero loop stays muted; the sound-on film opens over the page.
  const storyOn = filmEnabled();
  const [watching, setWatching] = useState(null); // null closed · { start, cut }
  const openWatch = (start, from) => {
    track('watch_open', { where: from });
    setWatching({ start, cut: 'watch' });
  };

  return (
    <PageWorld world={LANDING}>
    <div className="cs-store cs-land">
      {/* 1 — Hero: category, consequence, one primary CTA. With the hero
          film the copy is a lower-third block over the stage; without it
          the 3D restaging sits in the column. */}
      <header className={`cs-store-hero cs-land-hero${filmOn ? ' has-film' : ''}`}>
        {filmOn && <HeroFilm alt="Inputs flow through nQube and agentOS to the gate, then to your system or your App Kit" />}
        <div className="cs-land-hero-copy">
          {/* Over the film the block is a wide, short lower-third: the lead
              (logo, H1, sub-line, play row) on the left, the plan picker and
              the NoVA prompt on the right, the stat row as a bar underneath
              ([[homepage-conversion-first]], paid above the fold). */}
          <div className="cs-land-hero-lead">
            <img src="/logo.png" alt="nventr" className="cs-store-hero-logo" />
            <h1>AI agents that write to your ERP, CRM and apps &mdash; with a person or a rule in the loop.</h1>
            <p>
              Design the workflow in nQube; agentOS runs it. The agent proposes
              the work and cannot write it &mdash; a person or a simple rule says
              yes, and the runtime writes, with a receipt.
            </p>
            <div className="cs-land-watch">
              {storyOn && (
                <button type="button" className="cs-land-watch-btn" onClick={() => openWatch(0, 'hero')}>
                  <PlayGlyph />
                  Watch the film <span>· {FILM_LENGTH}</span>
                </button>
              )}
              <PlanLink tier={platformFree} where="hero" className="cs-land-proof-link">{platformFree.cta} — no card →</PlanLink>
            </div>
          </div>
          <div className="cs-land-pick">
            <div className="cs-land-pick-card is-main">
              <span>{platformStart.product} · {platformStart.price}</span>
              <small>Runs in production: 3,000 workflow runs, production connectors, bring your own model.</small>
              <PlanLink tier={platformStart} where="hero" />
              <HowLink product={PRODUCT_BY_ID.platform} where="hero" className="cs-land-proof-link cs-land-pick-how" />
            </div>
            <div className="cs-land-pick-card">
              <span>{appKit.product} · {appKit.price}</span>
              <small>Your URL, database and layer repo. Dev Console included.</small>
              <PlanLink tier={appKit} where="hero" className="cs-btn cs-btn-outline" />
              <HowLink product={PRODUCT_BY_ID.kit} where="hero" className="cs-land-proof-link cs-land-pick-how" />
            </div>
          </div>
          <form className="cs-land-need" onSubmit={askNeed} role="search" aria-label="Tell NoVA what you need">
            <label htmlFor="land-need" className="cs-land-need-label">Or tell NoVA what you need</label>
            <div className="cs-land-need-row">
              <input id="land-need" className="cs-input" value={need} onChange={(e) => setNeed(e.target.value)} onFocus={() => setNeedFocus(true)} onBlur={() => setNeedFocus(false)} placeholder={NEEDS[needIdx]} maxLength={400} />
              <button type="submit" className="cs-btn cs-btn-outline" disabled={!need.trim()}>Ask</button>
            </div>
            <span className="cs-land-need-hint">NoVA answers out loud. Signed in, it builds it in your organization.</span>
            <PitchButton hint="Two minutes, interrupt any time." />
          </form>
          <div className="cs-land-hero-stats">
            <div><strong>Cancel anytime</strong><span>monthly, no contract</span></div>
            <div><strong>Every write</strong><span>gated, logged, attributable</span></div>
            <div><strong>Not allowed</strong><span>= not possible</span></div>
          </div>
          {!filmOn && (
            <div className="cs-land-hero-art">
              <SceneFrame scene="hero" poster="/art/hero3d/hero-frame.webp" alt="Inputs flow through nQube and agentOS to the gate, then to your system or your App Kit" fallback={<HeroChassis />} aspect="840 / 336" />
              <p className="cs-land-hero-os">agentOS</p>
            </div>
          )}
        </div>
      </header>
      {storyOn && (
        <FilmLightbox
          open={watching !== null}
          start={watching ? watching.start : 0}
          cut={watching ? watching.cut : 'watch'}
          onCut={(next) => setWatching((w) => (w ? { ...w, cut: next } : w))}
          onClose={() => setWatching(null)}
        />
      )}

      {/* 2 — Trust band: measured on our own fleet, source on screen. No
          logos, no stars — there are none to quote, and we do not invent. */}
      <section className="cs-store-section cs-land-trust-section" id="measured">
        <ul className="cs-land-measures cs-land-trust">
          {MEASURES.map((m) => (
            <li key={m.k}>
              <strong>{m.v}</strong>
              <span className="cs-land-measure-k">{m.k}</span>
              <MeasureGlyph kind={m.g} />
              <span className="cs-land-measure-d">{m.d}</span>
              <small>{m.src}</small>
            </li>
          ))}
        </ul>
        <p className="cs-land-micro">
          Measured {MEASURED.measuredAt} on nventr&rsquo;s own fleet &mdash; nobody&rsquo;s
          customer data. Anything we could not measure is not on this page.{' '}
          {storyOn && <button type="button" className="cs-land-inline-btn" onClick={() => openWatch(numbersChapter().at, 'measured')}>Watch how each number was taken · {numbersChapter().length}</button>}
          {' '}Design-partner stories are being written with the teams running this today; until each one signs off, we show the measurements.
        </p>
      </section>

      {/* 3 — Who runs what: the buyer's jobs, each one ending in the CTA. */}
      <section className="cs-store-section" id="jobs">
        <span className="cs-mod-kicker">01 · Bridge</span>
        <h2>Finance can post the invoice. Ops can close the ticket. Shared services can pay the claim.</h2>
        <div className="cs-land-jobs cs-land-jobs-cta">
          {JOBS.map((j) => (
            <div key={j.team} className="cs-sw-card">
              <h3>{j.team} can {j.can}</h3>
              <p>{j.flow}</p>
              <PlanLink tier={platformStart} where={`jobs:${j.team}`} className="cs-land-proof-link">{platformStart.cta} →</PlanLink>
            </div>
          ))}
        </div>
        <p className="cs-land-micro">
          Built for operations, finance, and platform teams who can&rsquo;t
          let an agent write to production on a prompt.
        </p>
      </section>

      {/* 4 — See it run: one invoice through the four console screens. */}
      <section className="cs-store-section" id="proof">
        <span className="cs-mod-kicker">02 · Govern</span>
        <h2>One invoice, four screens, replayed from the console</h2>
        <p className="cs-store-note" style={{ maxWidth: 860, marginBottom: 16 }}>
          The agent reads it, a person holds the big one, the runtime posts
          it, the receipt stays on the record. Hover a step to scrub.
        </p>
        <SpineRun steps={INVOICE} />
      </section>

      {/* 5 — Why nventr: three checkable differences, one sentence each. */}
      <section className="cs-store-section" id="proofs">
        <h2>Most tools let the model ask to write. We never give the model the ability to write.</h2>
        <div className="cs-land-proofs">
          {PROOFS.map((p) => (
            <article key={p.k} className="cs-sw-card">
              <h3>{p.k}</h3>
              <p>{p.v}</p>
              <Link to={p.to} className="cs-land-proof-link">{p.cta} →</Link>
            </article>
          ))}
        </div>
      </section>

      {/* 6 — The two products, each a door into its page: Platform is what
          the invoice just ran on; App Kit is the app. Copy from data/spine.js
          so /platform and /app-kit open on the same words. */}
      <section className="cs-store-section" id="products">
        <span className="cs-mod-kicker">03 · Build</span>
        <h2>Two products. The workflow, or the app.</h2>
        <div className="cs-land-products">
          {PRODUCTS.map((p) => {
            const tier = p.id === 'kit' ? appKit : platformStart;
            const m = MEASURE_BY_G[p.measure];
            return (
              <article key={p.id} className={`cs-sw-card cs-land-product${p.id === 'platform' ? ' cs-land-featured' : ''}`}>
                <span className="cs-mod-kicker">{tier.product} · {tier.price}</span>
                <h3>{p.name}</h3>
                <p>{p.lead}</p>
                <p className="cs-land-product-parts">{p.parts.join(' · ')}</p>
                {m && (
                  <div className="cs-land-product-measure">
                    <strong>{m.v}</strong>
                    <span>{m.k}</span>
                    <MeasureGlyph kind={m.g} />
                  </div>
                )}
                <div className="cs-land-cta-row">
                  <PlanLink tier={tier} where="products" className={`cs-btn ${p.id === 'platform' ? 'cs-btn-primary' : 'cs-btn-outline'}`} />
                  <HowLink product={p} where="products" />
                </div>
              </article>
            );
          })}
        </div>
        <p className="cs-land-micro">
          Both run on agentOS. nQube designs the workflow on Platform and
          inside an App Kit app.{' '}
          <Link to="/pricing">Full pricing →</Link>
        </p>
      </section>

      {/* 7 — Pick your start: Start featured, App Kit and Free beside it. */}
      <section className="cs-store-section" id="offer">
        <h2>Start for {dollars(platformStart)}. Rent App Kit when you need the app. Try the canvas free.</h2>
        <SceneFrame
          scene="split"
          poster="/art/hero3d/split-poster.jpg"
          alt="Platform: a document passes a check and the gate into your ERP or CRM and leaves a receipt. App Kit: the App Builder around a dedicated instance — design canvas, web, desktop and phone surfaces, UAT twin promoted to prod, layer repo and Dev Console."
          aspect="840 / 336"
          className="cs-scene-section"
        />
        <div className="cs-land-price-grid cs-land-offer cs-land-offer-3">
          {[platformStart, appKit, platformFree].map((t) => (
            <div key={t.id} className={`cs-sw-card${t.id === 'start' ? ' cs-land-featured' : ''}`}>
              <h3>{t.product}</h3>
              <div className="cs-sw-price">{t.price}</div>
              <ul className="cs-product-specs">
                {t.bundle.map((b) => <li key={b}>{b}</li>)}
              </ul>
              <small>{t.helper}</small>
              <div className="cs-land-cta-row">
                <PlanLink tier={t} where="offer" className={`cs-btn ${t.id === 'start' ? 'cs-btn-primary' : 'cs-btn-outline'}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="cs-sw-card cs-land-one-workflow">
          <div>
            <h3>Start with one workflow</h3>
            <p>
              Bring the one job your team does by hand that should not run on
              a prompt. We wire it with you on your systems, gated, and you
              watch it commit.
            </p>
          </div>
          <a href={`${PILOT_MAILTO.split('?')[0]}?subject=${encodeURIComponent('Start with one workflow')}&body=${encodeURIComponent('The workflow: \nThe systems it writes to: \nWho reviews today: ')}`} className="cs-btn cs-btn-outline">
            Tell us the workflow
          </a>
        </div>
        <dl className="cs-land-works-rows">
          {WORKS_WITH.map((w) => (
            <div key={w.k}>
              <dt>{w.k}</dt>
              <dd>{w.items.join(' · ')}</dd>
            </div>
          ))}
        </dl>
        <p className="cs-land-micro">
          Credentials go to the secret store, never into the model. Dev
          Console comes with the Kit rent. Team is $39/mo for workflow seats;
          Kit and Team together list as $160/mo.{' '}
          <Link to="/pricing">Full pricing →</Link>
        </p>
      </section>

      {/* 8 — Final CTA. */}
      <section className="cs-land-band" id="start">
        <span className="cs-mod-kicker">04 · One brain</span>
        <h2>Start for {dollars(platformStart)} a month. Cancel anytime.</h2>
        <p>
          What you connect and what you build stay in one place &mdash; the
          organization&rsquo;s, a team&rsquo;s, an app&rsquo;s, or the instance
          you host.
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <PlanLink tier={platformStart} where="final" />
          <PlanLink tier={appKit} where="final" className="cs-btn cs-btn-outline" />
          <Link to={SALES_TO} className="cs-btn cs-btn-outline" onClick={() => track('cta_click', { where: 'final', label: 'Talk to sales' })}>
            Talk to sales
          </Link>
        </div>
        <p className="cs-land-micro" style={{ textAlign: 'center' }}>
          Not ready to buy? <PlanLink tier={platformFree} where="final" className="cs-land-proof-link">{platformFree.cta} — no card →</PlanLink>
        </p>
      </section>

      <footer className="cs-land-foot">
        <div className="cs-land-foot-cols">
          <div>
            <h4>Platform</h4>
            <Link to="/platform">Overview</Link>
            <Link to="/platform/nqube">nQube</Link>
            <Link to="/platform/agentos">agentOS</Link>
            <Link to="/platform/agent-io">Agent IO</Link>
            <Link to="/platform/nventr-agent">nventr Agent</Link>
            <Link to="/platform/athena">Athena</Link>
            <Link to="/platform/listen">Listen</Link>
            <Link to="/platform/reachout">Reachout</Link>
            <Link to="/platform/devops">DevOps</Link>
            <Link to="/platform#whats-new">What&rsquo;s new</Link>
          </div>
          <div>
            <h4>App Kit</h4>
            <Link to="/app-kit">Overview</Link>
            <Link to="/platform/dev-console">Dev Console</Link>
            <Link to="/app-kit#foundation">The foundation</Link>
            <Link to="/app-kit#assurance">Assurance</Link>
            <Link to="/app-kit#fleet">The fleet</Link>
            <Link to="/pricing">Pricing</Link>
          </div>
          <div>
            <h4>Company</h4>
            <Link to="/support">Support</Link>
            <Link to="/physical">GridOS</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/eula">End user agreement</Link>
            <Link to="/security">Security</Link>
            <a href={NVENTR_AI} target="_blank" rel="noreferrer">
              nventr.ai
            </a>
          </div>
          <div>
            <h4>Partners</h4>
            <Link to="/partners">Edge OS · telcos &amp; ISPs</Link>
            <Link to="/store">Hardware</Link>
            <Link to="/plan">Partner sign-in</Link>
          </div>
        </div>
        <p className="cs-land-foot-tag">
          nventr — Design in nQube. Build in Dev Console. Run on agentOS.
        </p>
      </footer>
    </div>
    </PageWorld>
  );
}
